import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Attachment } from '@runloop/reflex-contract';
import { getApiUrl, authHeaders, IS_CROSS_ORIGIN } from '@reflex/ui/client/http';

interface AttachmentUrlResult {
  url: string | null;
  isExpired: boolean;
  isLoading: boolean;
  /** Invalidate the cached presigned URL and fetch a fresh one. */
  refresh: () => void;
}

async function fetchUploadUrl(id: string): Promise<{ url: string; expiresAt?: number }> {
  const res = await fetch(getApiUrl(`/uploads/${encodeURIComponent(id)}/url`), {
    headers: authHeaders(),
    ...(IS_CROSS_ORIGIN && { credentials: 'include' as const }),
  });

  if (res.status === 410) {
    throw new ExpiredError();
  }

  if (!res.ok) {
    throw new Error(`Failed to resolve attachment URL: ${res.status}`);
  }

  return res.json() as Promise<{ url: string; expiresAt?: number }>;
}

class ExpiredError extends Error {
  constructor() {
    super('Attachment expired');
    this.name = 'ExpiredError';
  }
}

function storageIdFor(attachment: Attachment): string | undefined {
  return (
    attachment.storageObjectId ?? (attachment.id && !attachment.url ? attachment.id : undefined)
  );
}

function urlQueryOptions(attachment: Attachment) {
  const storageId = storageIdFor(attachment);
  return {
    // An attachment that already carries a URL never fetches, but it still
    // needs a key of its own: two inline attachments resolved side by side
    // through `useAttachmentUrls` would otherwise collide on one key.
    queryKey: ['attachment-url', storageId ?? `inline:${attachment.id}`],
    queryFn: () => fetchUploadUrl(storageId!),
    enabled: !!storageId && !attachment.url,
    staleTime: 30 * 60 * 1000, // 30 min (presigned URL valid for 1 hour)
    retry: (count: number, err: Error) => {
      if (err instanceof ExpiredError) return false;
      return count < 2;
    },
  };
}

function toResult(
  attachment: Attachment,
  query: { data?: { url: string }; isLoading: boolean; error: unknown },
  refresh: () => void,
): AttachmentUrlResult {
  if (attachment.url) {
    return { url: attachment.url, isExpired: false, isLoading: false, refresh: NOOP };
  }
  if (query.error instanceof ExpiredError) {
    return { url: null, isExpired: true, isLoading: false, refresh: NOOP };
  }
  return { url: query.data?.url ?? null, isExpired: false, isLoading: query.isLoading, refresh };
}

const NOOP = () => {};

/**
 * Resolves a storage object attachment to a presigned download URL.
 * Returns the URL inline if it already exists (legacy data URLs or previously resolved).
 * For storage-backed attachments, fetches a presigned URL with caching.
 */
export function useAttachmentUrl(attachment: Attachment): AttachmentUrlResult {
  const queryClient = useQueryClient();
  const storageId = storageIdFor(attachment);

  const query = useQuery(urlQueryOptions(attachment));

  const refresh = useCallback(() => {
    if (storageId) {
      void queryClient.invalidateQueries({ queryKey: ['attachment-url', storageId] });
    }
  }, [queryClient, storageId]);

  return toResult(attachment, query, refresh);
}

/**
 * The list form of {@link useAttachmentUrl}: resolves a whole set of
 * attachments in one hook so a caller can offer them as a single gallery.
 * Results are positional — index `i` describes `attachments[i]`.
 */
export function useAttachmentUrls(attachments: Attachment[]): AttachmentUrlResult[] {
  const queryClient = useQueryClient();

  const queries = useQueries({ queries: attachments.map(urlQueryOptions) });

  const refresh = useCallback(
    (storageId: string | undefined) => () => {
      if (storageId) {
        void queryClient.invalidateQueries({ queryKey: ['attachment-url', storageId] });
      }
    },
    [queryClient],
  );

  return attachments.map((attachment, i) =>
    toResult(attachment, queries[i]!, refresh(storageIdFor(attachment))),
  );
}
